const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

const HEADING_IS_MEANINGLESS_BELOW = 0.08;
const SMOOTHING = 0.35;

export interface DeviceAngles {
  alpha: number;
  beta: number;
  gamma: number;
}

const cameraAxis = ({ alpha, beta, gamma }: DeviceAngles) => {
  const a = alpha * RAD;
  const b = beta * RAD;
  const g = gamma * RAD;
  const cosA = Math.cos(a);
  const sinA = Math.sin(a);
  const cosB = Math.cos(b);
  const sinB = Math.sin(b);
  const cosG = Math.cos(g);
  const sinG = Math.sin(g);

  return {
    east: -(cosA * sinG + cosG * sinA * sinB),
    north: -(sinA * sinG - cosA * cosG * sinB),
    up: -(cosB * cosG),
  };
};

export const normalizeAngle = (degrees: number): number => ((degrees % 360) + 360) % 360;

export const cameraHeading = (angles: DeviceAngles): number | null => {
  const axis = cameraAxis(angles);
  const horizontal = Math.hypot(axis.east, axis.north);
  if (horizontal < HEADING_IS_MEANINGLESS_BELOW) {
    return null;
  }

  return normalizeAngle(Math.atan2(axis.east, axis.north) * DEG);
};

export const smoothedTowards = (previous: number | null, next: number): number => {
  if (previous === null) {
    return next;
  }
  const shortestTurn = ((next - previous + 540) % 360) - 180;

  return normalizeAngle(previous + shortestTurn * SMOOTHING);
};

export const anglesFromEvent = (event: DeviceOrientationEvent): DeviceAngles | null => {
  const compass = (event as DeviceOrientationEvent & { webkitCompassHeading?: number }).webkitCompassHeading;
  const isTrueCompass = typeof compass === 'number' && Number.isFinite(compass);
  if (!isTrueCompass && !event.absolute) {
    return null;
  }
  const alpha = isTrueCompass ? 360 - compass : event.alpha;
  if (alpha === null || !Number.isFinite(alpha)) {
    return null;
  }

  return { alpha, beta: event.beta ?? 0, gamma: event.gamma ?? 0 };
};
